import React, { useState, useMemo } from 'react';
import ReportPage from '../../../Components/crude/ReportPage';
import useReportData from '../../../Components/crude/useReportData';
import {
  REPORT_ENDPOINTS,
  EMPTY_REPORT_FILTERS,
  yieldLabel,
} from '../../../Components/crude/reportConstants';

// Facility Report — GET /v1/Report/GetFacilityReports (rhacrude_api).
// Menu label: Reception Facility ("Fasilitas Penerimaan Minyak Mentah").
//
// The limit columns are dynamic: one group per plant, one column per reception
// facility (Jetty / CIB / SPM ...), sourced from master RefineryUnit +
// ReceptionFacility. Never hardcode them — the skeleton comes from the response
// and CHANGES when a Plant filter is applied (the API then returns only that
// plant), so the header is re-derived from `items` on every fetch.
//
// Each cell is a tri-state `isWithinLimit`:
//   true  -> ✔ (pourPoint <= facility maxPourPoint)
//   false -> ✖ (pourPoint >  facility maxPourPoint)
//   null  -> blank (no implementation history, or the crude has no pourPoint)
//
// Sortable: only the 5 displayed columns. Country/SulfurClassification are in
// the response but are NOT grid columns and return 400 if sent as sortField.
const withinLimitCell = value => {
  if (value === true) return <span className='text-success'>✔</span>;
  if (value === false) return <span className='text-danger'>✖</span>;
  return '';
};

const FacilityReportView = () => {
  const [filters, setFilters] = useState(EMPTY_REPORT_FILTERS);

  const {
    items,
    totalRows,
    loading,
    page,
    perPage,
    sortField,
    sortDirection,
    setPage,
    setPerPage,
    handleSort,
  } = useReportData({ endpoint: REPORT_ENDPOINTS.FACILITY.LIST, filters });

  const columns = useMemo(() => {
    // The plant/facility skeleton is the same for every row in a response.
    const plants = items[0]?.plants ?? [];

    const plantColumns = plants.map(plant => ({
      header: plant.plantName,
      children: (plant.facilities ?? []).map(facility => ({
        key: `${plant.id}-${facility.id}`,
        // Show the limit the ✔/✖ is measured against.
        header: `${facility.facilityName} (${facility.maxPourPoint} °C)`,
        sortable: false,
        align: 'center',
        accessor: row => {
          const f = row.plants
            ?.find(p => p.id === plant.id)
            ?.facilities?.find(x => x.id === facility.id);
          return withinLimitCell(f?.isWithinLimit ?? null);
        },
      })),
    }));

    return [
      {
        key: 'CrudeName',
        header: 'Nama Minyak Mentah',
        accessor: row => row.crudeName,
      },
      {
        key: 'AssayYear',
        header: 'Tahun Assay',
        accessor: row => row.assayYear,
        align: 'center',
      },
      {
        key: 'YieldClassification',
        header: 'Klasifikasi Yield',
        accessor: row => yieldLabel(row.yieldClassification),
      },
      {
        header: 'Pour Point',
        children: [
          {
            key: 'PourPoint',
            header: '(°C)',
            accessor: row => row.pourPoint,
            align: 'center',
          },
          {
            key: 'PourPointFahrenheit',
            header: '(°F)',
            // Computed by the backend — do not derive from °C here.
            accessor: row => row.pourPointFahrenheit,
            align: 'center',
          },
        ],
      },
      ...plantColumns,
    ];
  }, [items]);

  return (
    <ReportPage
      title='Facility Report'
      cardTitle='Reception Facility Report'
      documentTitle='Facility Report'
      columns={columns}
      data={items}
      loading={loading}
      totalRows={totalRows}
      page={page}
      perPage={perPage}
      sortField={sortField}
      sortDirection={sortDirection}
      onChangePage={setPage}
      onChangeRowsPerPage={newPerPage => {
        setPerPage(newPerPage);
        setPage(1);
      }}
      onSort={handleSort}
      filters={filters}
      onFiltersChange={setFilters}
      endpoints={REPORT_ENDPOINTS.FACILITY}
      exportBaseName='facility-report'
      emptyText='No facility report data found'
    />
  );
};

export default FacilityReportView;
